import React from 'react';
import * as stylex from '@stylexjs/stylex';
import { useButton, useHover, useLink, mergeProps } from 'react-aria';
import '../theme/Colors.stylex';
import { ColorNeutral, ColorBrand } from '../theme/Colors.stylex';
import '../theme/Spaces.stylex';
import { Gap } from '../theme/Spaces.stylex';
import '../theme/Fonts.stylex';
import { FontFamily, FontSize, FontWeight } from '../theme/Fonts.stylex';
import { Icon } from '../images-and-icons/Icon';
import { Tooltip, TooltipSpan } from '../overlays/Tooltip';
import { Title } from '../typography/Title';
import { t } from 'src/primitives/I18n';

// MARK: CopyButton
// ================
type CopyButtonProps = {
  value: string;
};
function CopyButton({
  value
}: CopyButtonProps) {
  const ref = React.useRef<HTMLButtonElement>(null);
  const [copied, setCopied] = React.useState(false);
  const {
    buttonProps
  } = useButton({
    isDisabled: copied,
    'aria-label': 'copy',
    onPress: () => {
      navigator.clipboard.writeText(value);
      setCopied(true);
    }
  }, ref);
  const {
    hoverProps,
    isHovered
  } = useHover({});
  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  return <Tooltip content={<TooltipSpan text={copied ? t('Copied to clipboard!') : t('Copy')} />} placement="top" delay={copied ? 0 : 350} closeDelay={0}>
      <button ref={ref} {...mergeProps(buttonProps, hoverProps)} className="display-x78zum5 alignItems-x6s0dn4 justifyContent-xl56j7k padding-x1717udv transform-xipoby6 backgroundColor-xjbqb8w" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:362">
        <Icon name={copied ? 'tick' : 'duplicate_v1'} width={14} height={14} fill={copied || isHovered ? ColorNeutral.iconAccent : ColorNeutral.iconDefault} bold={copied} />
      </button>
    </Tooltip>;
}

// MARK: ValueLink
// ===============
type ValueLinkProps = {
  value: string;
  href: string;
};
function ValueLink({
  value,
  href
}: ValueLinkProps) {
  const ref = React.useRef<HTMLAnchorElement>(null);
  const {
    linkProps
  } = useLink({
    href,
    target: '_blank'
  }, ref);
  const {
    hoverProps,
    isHovered
  } = useHover({});
  return <a ref={ref} href={href} target="_blank" rel="noopener noreferrer" title={value} {...mergeProps(linkProps, hoverProps)} {...{
    0: {
      className: "minWidth-xeuugli overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft fontFamily-x1q3ln6d fontSize-x1egw0ox lineHeight-x1d3mw78 letterSpacing-x1owzkzt fontWeight-x1fkahbb textAlign-x1hr2gdg color-x1mzc8w9 textDecoration-x1hl2dhg cursor-x1ypdohk",
      "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:287; wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:301; wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:305"
    },
    1: {
      className: "minWidth-xeuugli overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft fontFamily-x1q3ln6d fontSize-x1egw0ox lineHeight-x1d3mw78 letterSpacing-x1owzkzt fontWeight-x1fkahbb textAlign-x1hr2gdg color-x1mzc8w9 cursor-x1ypdohk textDecoration-x1bvjpef",
      "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:287; wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:301; wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:305; wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:310"
    }
  }[!!isHovered << 0]}>
      {value}
    </a>;
}

// MARK: ValueBadge
// =================
type ValueBadgeProps = {
  value: string;
  backgroundColor?: string;
  foregroundColor?: string;
  borderColor?: string;
};
function ValueBadgeComponent({
  value,
  backgroundColor = ColorNeutral.fillDefault,
  foregroundColor = ColorNeutral.textAccent,
  borderColor = ColorNeutral.borderDefault
}: ValueBadgeProps) {
  return <div {...stylex.props(styles.ValueBadge_root, dynamicStyles.ValueBadge_root(backgroundColor, borderColor))}>
      <span {...stylex.props(styles.ValueBadge_text, dynamicStyles.ValueBadge_text(foregroundColor))}>
        {value}
      </span>
    </div>;
}
const MemoizedValueBadgeComponent = React.memo(ValueBadgeComponent);
export { MemoizedValueBadgeComponent as InformationListValueBadge };
export type { ValueBadgeProps };

// MARK: CopySlotContext
// ======================
type CopySlotContextValue = {
  register: () => () => void;
  hasAnyCopyButton: boolean;
};
const CopySlotContext = React.createContext<CopySlotContextValue | null>(null);

// MARK: Line
// ==========
type InformationListLineProps = {
  label: string;
  value?: string;
  valueElement?: React.ReactNode;
  href?: string;
  hideCopyButton?: boolean;
};
function InformationListLineComponent({
  label,
  value,
  valueElement,
  href,
  hideCopyButton = false
}: InformationListLineProps) {
  const copySlotContext = React.useContext(CopySlotContext);
  const register = copySlotContext?.register;
  const hasCopyButton = value != null && !hideCopyButton;
  React.useEffect(() => {
    if (!hasCopyButton || register == null) return;
    return register();
  }, [hasCopyButton, register]);
  const showCopySlot = copySlotContext != null ? copySlotContext.hasAnyCopyButton : hasCopyButton;
  let content: React.ReactNode;
  if (valueElement != null) {
    content = valueElement;
  } else if (value != null && href != null) {
    content = <ValueLink value={value} href={href} />;
  } else if (value != null) {
    content = <span title={value} className="minWidth-xeuugli overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft fontFamily-x1q3ln6d fontSize-x1egw0ox lineHeight-x1d3mw78 letterSpacing-x1owzkzt color-x1w1nkkj fontWeight-x1fkahbb textAlign-x1hr2gdg" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:287; wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:301">
        {value}
      </span>;
  } else {
    content = <span className="fontFamily-x1q3ln6d fontSize-x1egw0ox lineHeight-x1d3mw78 fontStyle-x1k4tb9n color-xyemlsm" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:320">{t('N/C')}</span>;
  }
  return <div className="display-xjp7ctv" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:284">
      <span title={label} className="minWidth-xeuugli overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft fontFamily-x1q3ln6d fontSize-x1egw0ox lineHeight-x1d3mw78 letterSpacing-x1owzkzt color-x1w1nkkj fontWeight-x168tpfk" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:287; wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:298">
        {label}
      </span>
      <div className="display-x78zum5 minWidth-xeuugli justifyContent-x7v6yn8 alignItems-x6s0dn4 gap-xt0mmm2" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:313">
        {content}
        {showCopySlot ? <div className="display-x78zum5 alignItems-x6s0dn4 justifyContent-xl56j7k flexShrink-x2lah0s width-x1kky2od height-xlup9mm" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:327">
            {value != null && !hideCopyButton ? <CopyButton value={value} /> : null}
          </div> : null}
      </div>
    </div>;
}
const MemoizedInformationListLineComponent = React.memo(InformationListLineComponent);
export { MemoizedInformationListLineComponent as InformationListLine };
export type { InformationListLineProps };

// MARK: Gap
// =========
function InformationListGapComponent() {
  return <div className="gridColumn-x1osaytk height-xqtp20y" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:358" />;
}
const MemoizedInformationListGapComponent = React.memo(InformationListGapComponent);
export { MemoizedInformationListGapComponent as InformationListGap };

// MARK: root
// ==========
type InformationListProps = {
  title?: string;
  children: React.ReactNode;
};
function InformationListComponent({
  title,
  children
}: InformationListProps) {
  const [copyButtonCount, setCopyButtonCount] = React.useState(0);
  const register = React.useCallback(() => {
    setCopyButtonCount(count => count + 1);
    return () => setCopyButtonCount(count => count - 1);
  }, []);
  const copySlotContext = React.useMemo(() => ({
    register,
    hasAnyCopyButton: copyButtonCount > 0
  }), [register, copyButtonCount]);
  return <div className="display-x78zum5 width-xh8yej3 flexDirection-xdt5ytf gap-x7dls1a" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:270">
      {title != null ? <Title level={5} weight="semibold" color={ColorNeutral.textDefault}>
          {title}
        </Title> : null}
      <div className="display-xrvj5dj width-xh8yej3 gridTemplateColumns-xygxuo1 columnGap-x11eonz0 rowGap-x1rdo6bc alignItems-x6s0dn4" data-style-src="wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:276">
        <CopySlotContext.Provider value={copySlotContext}>
          {children}
        </CopySlotContext.Provider>
      </div>
    </div>;
}
const MemoizedInformationListComponent = React.memo(InformationListComponent);
export { MemoizedInformationListComponent as InformationList };
export type { InformationListProps };

// MARK: style:dynamic
// ===================
const dynamicStyles = {
  ValueBadge_root: (backgroundColor: string, borderColor: string) => [{
    "backgroundColor-kWkggS": backgroundColor != null ? "backgroundColor-xl8spv7" : backgroundColor,
    "borderColor-kVAM5u": borderColor != null ? "borderColor-x6w5c0t" : borderColor,
    $$css: "wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:258"
  }, {
    "--x-backgroundColor": backgroundColor != null ? backgroundColor : undefined,
    "--x-borderColor": borderColor != null ? borderColor : undefined
  }],
  ValueBadge_text: (color: string) => [{
    "color-kMwMTN": color != null ? "color-x14rh7hd" : color,
    $$css: "wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:262"
  }, {
    "--x-color": color != null ? color : undefined
  }]
};

// MARK: style
// ===========
const styles = {
  ValueBadge_root: {
    "display-k1xSpc": "display-x78zum5",
    "alignItems-kGNEyG": "alignItems-x6s0dn4",
    "justifyContent-kjj79g": "justifyContent-xl56j7k",
    "gap-kOIVth": "gap-xgolt85",
    "minHeight-kAzted": "minHeight-xisnujt",
    "height-kZKoxP": "height-x1qx5ct2",
    "paddingInline-kg3NbH": "paddingInline-x1xijbu4",
    "paddingBlock-k8WAf4": "paddingBlock-x12ulsxz",
    "borderRadius-kaIpWk": "borderRadius-x12oqio5",
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:335"
  },
  ValueBadge_text: {
    "minWidth-k7Eaqz": "minWidth-xeuugli",
    "overflow-kVQacm": "overflow-xb3r6kr",
    "textOverflow-kg5iWk": "textOverflow-xlyipyv",
    "whiteSpace-khDVqt": "whiteSpace-xuxw1ft",
    "fontFamily-kMv6JI": "fontFamily-x111x8ze",
    "fontSize-kGuDYH": "fontSize-x106xqen",
    "fontWeight-k63SB2": "fontWeight-x1fkahbb",
    "textAlign-k9WMMc": "textAlign-x2b8uid",
    $$css: "wino-gc:src/resources/data-visualizations/CanaryInformationList.tsx:348"
  }
};